import {
  lookupBridgeRule,
  parseBridgeMatrix as parseCoreBridgeMatrix,
} from "@csk/core";
import type {
  BridgeMatrix as CoreBridgeMatrix,
  BridgeRuleStatus,
} from "@csk/core";
import type { BridgeMatrix, MatrixRule } from "./types";

export interface BridgeMatrixEdge {
  readonly to: string;
  readonly status: BridgeRuleStatus;
}

export interface BridgeMatrixIndex {
  readonly nodes: readonly string[];
  readonly adjacency: ReadonlyMap<string, readonly BridgeMatrixEdge[]>;
  readonly allowedAdjacency: ReadonlyMap<string, readonly string[]>;
  readonly ruleByPair: ReadonlyMap<string, MatrixRule>;
}

export interface NormalizedBridgeMatrix {
  readonly core: CoreBridgeMatrix;
  readonly index: BridgeMatrixIndex;
}

function matrixRuleKey(lhs: string, rhs: string): string {
  return `${String(lhs.length)}:${lhs}${String(rhs.length)}:${rhs}`;
}

function addEdge(
  adjacency: Map<string, BridgeMatrixEdge[]>,
  lhs: string,
  edge: BridgeMatrixEdge,
): void {
  const edges = adjacency.get(lhs);
  if (edges === undefined) {
    adjacency.set(lhs, [edge]);
    return;
  }
  edges.push(edge);
}

function addAllowedEdge(
  adjacency: Map<string, string[]>,
  lhs: string,
  rhs: string,
): void {
  const edges = adjacency.get(lhs);
  if (edges === undefined) {
    adjacency.set(lhs, [rhs]);
    return;
  }
  edges.push(rhs);
}

export function buildBridgeMatrixIndex(
  rules: readonly MatrixRule[],
): BridgeMatrixIndex {
  const nodes = new Set<string>();
  const adjacency = new Map<string, BridgeMatrixEdge[]>();
  const allowedAdjacency = new Map<string, string[]>();
  const ruleByPair = new Map<string, MatrixRule>();

  for (const rule of rules) {
    nodes.add(rule.lhs);
    nodes.add(rule.rhs);
    addEdge(adjacency, rule.lhs, { to: rule.rhs, status: rule.status });
    if (rule.status === "allowed")
      addAllowedEdge(allowedAdjacency, rule.lhs, rule.rhs);
    const key = matrixRuleKey(rule.lhs, rule.rhs);
    if (!ruleByPair.has(key)) ruleByPair.set(key, rule);
  }

  return {
    nodes: Array.from(nodes).sort(),
    adjacency,
    allowedAdjacency,
    ruleByPair,
  };
}

export function normalizeBridgeMatrixRules(
  rules: readonly MatrixRule[],
  unknownPair: BridgeRuleStatus,
): NormalizedBridgeMatrix | null {
  const core = parseCoreBridgeMatrix({
    rules: rules.map((rule) => ({
      lhs: rule.lhs,
      rhs: rule.rhs,
      status: rule.status,
      requiredWitnessKinds: [],
      reason: rule.reason ?? rule.notes ?? null,
    })),
    unknownPair,
  });
  if (core === null) return null;
  return { core, index: buildBridgeMatrixIndex(rules) };
}

export function lookupMatrixRule(
  matrix: BridgeMatrix,
  lhs: string,
  rhs: string,
): MatrixRule | null {
  return matrix.index.ruleByPair.get(matrixRuleKey(lhs, rhs)) ?? null;
}

export function lookupMatrixStatus(
  matrix: BridgeMatrix,
  lhs: string,
  rhs: string,
): BridgeRuleStatus {
  return lookupBridgeRule(matrix.core, lhs, rhs).status;
}

export function shortestAllowedPath(
  index: BridgeMatrixIndex,
  start: string,
  goal: string,
): string[] | null {
  const queue: string[] = [start];
  let head = 0;
  const previous = new Map<string, string | null>();
  previous.set(start, null);

  while (head < queue.length) {
    const current = queue[head++];
    if (current === goal) break;
    for (const next of index.allowedAdjacency.get(current) ?? []) {
      if (!previous.has(next)) {
        previous.set(next, current);
        queue.push(next);
      }
    }
  }

  if (!previous.has(goal)) return null;
  const path: string[] = [];
  let current: string | null = goal;
  while (current !== null) {
    path.push(current);
    current = previous.get(current) ?? null;
  }
  return path.reverse();
}

export function withBridgeMatrixRules(
  matrix: BridgeMatrix,
  rules: MatrixRule[],
): BridgeMatrix {
  const normalized = normalizeBridgeMatrixRules(
    rules,
    matrix.unknown_pair ?? "forbidden",
  );
  if (normalized === null) {
    throw new Error("agent bridge matrix failed core normalization");
  }
  return { ...matrix, rules, core: normalized.core, index: normalized.index };
}
